import { isOnlyXYInclined } from '@scene'
import { filter, find, first, groupBy, isUndefined, round, some, toNumber } from 'lodash-es'
import { Line3, Vector3 } from 'three'
import { ImmutableVector3, isPointOnLineSegment } from '@modugen/ui-toolkit'
import * as turf from '@turf/turf'
import { lineString } from '@turf/turf'
import { nthLooped } from '@utils'

// Exact supports, so cannot have greater or fewer than the number
export const elementTypeToRequiredSupport: { [type in ElementTypes]?: number } = {
  lintels: 2,
  rips: 1,
}

const elementTypesNotAllowedWithoutLoadInducing = new Set<ElementTypes>([
  'beams',
  'columns',
  'purlins',
])

const intervalFractionDigits = 4
const wallOverlapThreshold = 0.01

interface AdmissibleSupportConfiguration {
  minPointSupports: number
  maxPointSupports: number
  minLineSupports: number
  maxLineSupports: number
  minFullySupportedDomains: number
  maxFullySupportedDomains: number
  partiallySupportedDomainsAllowed: boolean
  note: string
}

const createAdmissibleSupportConfig = (
  config: Partial<AdmissibleSupportConfiguration>,
): AdmissibleSupportConfiguration => ({
  minPointSupports: 0,
  maxPointSupports: Number.MAX_SAFE_INTEGER,
  minLineSupports: 0,
  maxLineSupports: Number.MAX_SAFE_INTEGER,
  minFullySupportedDomains: 0,
  maxFullySupportedDomains: Number.MAX_SAFE_INTEGER,
  partiallySupportedDomainsAllowed: true,
  note: '',
  ...config,
})

const exactlyNPointSupports = (count: number): AdmissibleSupportConfiguration =>
  createAdmissibleSupportConfig({
    minPointSupports: count,
    maxPointSupports: count,
    maxLineSupports: 0,
    note: `Genau ${count} Punktauflager`,
  })

const minNFullySupportedDomains = (count: number): AdmissibleSupportConfiguration =>
  createAdmissibleSupportConfig({
    minFullySupportedDomains: count,
    minLineSupports: 1,
    maxPointSupports: 0,
    note: `Mindestens ${count} durch Linienauflager vollständig abgedeckte Domains.`,
  })

const minNPointSupports = (count: number): AdmissibleSupportConfiguration =>
  createAdmissibleSupportConfig({
    minPointSupports: count,
    note: `Mindestens ${count} Punktauflager`,
  })

const minNLineSupports = (count: number): AdmissibleSupportConfiguration =>
  createAdmissibleSupportConfig({
    minLineSupports: count,
    note: `Mindestens ${count} Linienauflager`,
  })

const minNFullySupportedDomainsAndNoPartiallySupportedDomains = (
  count: number,
): AdmissibleSupportConfiguration =>
  createAdmissibleSupportConfig({
    minFullySupportedDomains: count,
    minLineSupports: 1,
    maxPointSupports: 0,
    partiallySupportedDomainsAllowed: false,
    note: `Mindestens ${count} durch Linienauflager vollständig abgedeckte Domains und alle weiteren Domains ebenfalls vollständig abgedeckt.`,
  })

const ADMISSIBLE_SUPPORTS: { [key in ElementTypes]?: AdmissibleSupportConfiguration[] } = {
  beams: [minNPointSupports(2), minNLineSupports(1)],
  purlins: [minNPointSupports(2), minNLineSupports(1)],
  columns: [exactlyNPointSupports(1)],
  rips: [exactlyNPointSupports(1)],
  lintels: [exactlyNPointSupports(2)],
  outer_walls: [minNFullySupportedDomains(1)],
  inner_walls: [minNFullySupportedDomains(1)],
  vertical_slabs: [minNFullySupportedDomainsAndNoPartiallySupportedDomains(2)],
  vertical_roof_slabs: [minNFullySupportedDomainsAndNoPartiallySupportedDomains(2)],
}

interface IntervalCheckResult {
  isValid: boolean
  errorMessage?: string
}

const checkIntervalUnionCoversCompleteWidth = (
  intervals: Interval[],
  elementLength?: number,
): IntervalCheckResult => {
  const sortedIntervals = intervals.sort((a, b) => toNumber(a.lower) - toNumber(b.lower))

  // Check if it starts at 0
  const lowerEnd = toNumber(sortedIntervals[0].lower)
  if (lowerEnd !== 0) {
    let errorMessage = 'Auflager beginnt nicht am Anfang der Domain.'

    if (!isUndefined(elementLength)) {
      errorMessage += `Es fehlen ${round(lowerEnd * elementLength, 2)}m.`
    }

    return {
      isValid: false,
      errorMessage,
    }
  }

  // Check if it ends at 1
  const upperEnd = toNumber(sortedIntervals[sortedIntervals.length - 1].upper)
  if (upperEnd !== 1) {
    let errorMessage = 'Auflager endet nicht am Ende der Domain.'

    if (!isUndefined(elementLength)) {
      errorMessage += ` Es fehlen ${round((1 - upperEnd) * elementLength, 2)}m.`
    }

    return {
      isValid: false,
      errorMessage,
    }
  }

  // Check for gaps between intervals
  for (let i = 0; i < sortedIntervals.length - 1; i++) {
    const currentUpper = toNumber(sortedIntervals[i].upper)
    const nextLower = toNumber(sortedIntervals[i + 1].lower)

    if (
      currentUpper.toFixed(intervalFractionDigits) !== nextLower.toFixed(intervalFractionDigits)
    ) {
      let errorMessage = 'Domain enthält Bereiche ohne Auflager.'

      if (!isUndefined(elementLength)) {
        errorMessage += ` Zwischen ${currentUpper * elementLength}m und ${
          nextLower * elementLength
        }`
      }

      return { isValid: false, errorMessage }
    }
  }

  return { isValid: true }
}

const checkIfRipIsIndirectlyUnsupported = (
  rip_support: ElementSupportItem,
  graph: VerticalTransmissionGraph,
  containing_wall: string,
): boolean => {
  const target_guid = find(graph.support_targets, {
    support_guid: rip_support.guid,
  })?.target_guid
  const target = find(graph.element_targets, { guid: target_guid })

  if (target && containing_wall === target.element_guid) {
    const wall_supports = filter(graph.element_supports, {
      element_guid: target.element_guid,
    })
    const rip_position = target.relative_position

    // Check if any wall_support's relative_interval includes the rip_position
    const isRipPositionSupported = wall_supports.some(support => {
      const interval = support.relative_interval as Interval
      return (
        toNumber(interval.lower) <= toNumber(rip_position) &&
        toNumber(rip_position) <= toNumber(interval.upper)
      )
    })

    return !isRipPositionSupported
  } else {
    // Rip is directly supported by some element other than its containing wall
    return false
  }
}

/**
 * shorten a line on both ends by a distance in meter
 * @param line line to shorten
 * @param shortenBy distance to shorten (in meter) on end and start of line
 * @returns new line with shortened start and end
 */
function shortenLine(line: Line3, shortenBy: number) {
  const distanceFactor = shortenBy / line.distance()
  const newStart = line.at(distanceFactor, new Vector3())
  const newEnd = line.at(1 - distanceFactor, new Vector3())

  return new Line3(newStart, newEnd)
}

/**
 * Check whether lines are overlapping (not intersecting), with a small threshold
 */
function linesOverlapping(line1: Line3, line2: Line3, threshold = 0.05) {
  // as the isPointOnLineSegment check below also returns true if a line point
  // is within the threshold distance at the start and end of line (before and
  // after), we need to shorten the line a bit by the threshold distance on both ends
  const line1Shortened = shortenLine(line1, threshold)
  const line2Shortened = shortenLine(line2, threshold)

  const anyLine1PointOnLine2 =
    isPointOnLineSegment(line2Shortened, line1Shortened.start, threshold) ||
    isPointOnLineSegment(line2Shortened, line1Shortened.end, threshold)

  const anyLine2PointOnLine1 =
    isPointOnLineSegment(line1Shortened, line2Shortened.start, threshold) ||
    isPointOnLineSegment(line1Shortened, line2Shortened.end, threshold)

  return anyLine2PointOnLine1 && anyLine1PointOnLine2
}

const isWallIntersectingWithOtherWalls = (
  wall: ShapeObject,
  allElements: Record<string, ShapeObject>,
  guidToElementType: Record<string, ElementTypes | undefined>,
): boolean => {
  const [start, end] = wall.shape.points
  const editedWallLine = lineString([
    [start.x, start.y],
    [end.x, end.y],
  ])
  const editedWallLine3 = new Line3(new Vector3(start.x, start.y), new Vector3(end.x, end.y))

  const wallsOnSameStorey = Object.values(allElements).filter(otherWall => {
    if (otherWall.guid === wall.guid || otherWall.storey !== wall.storey) {
      return false
    }

    return isWallType(guidToElementType[otherWall.guid] as ElementTypes)
  })

  return wallsOnSameStorey.some(otherWall => {
    const [wallStartPoint, wallEndPoint] = otherWall.shape.points
    const wallLine = lineString([
      [wallStartPoint.x, wallStartPoint.y],
      [wallEndPoint.x, wallEndPoint.y],
    ])
    const wallLineThree = new Line3(
      new Vector3(wallStartPoint.x, wallStartPoint.y),
      new Vector3(wallEndPoint.x, wallEndPoint.y),
    )

    const overlap = linesOverlapping(editedWallLine3, wallLineThree)
    if (overlap) return true

    const intersectedFeature = first(turf.lineIntersect(editedWallLine, wallLine).features)

    if (!intersectedFeature) return false

    const intersectedPoint = new Vector3(
      intersectedFeature.geometry.coordinates[0],
      intersectedFeature.geometry.coordinates[1],
    )

    const wallEnds = [...editedWallLine.geometry.coordinates, ...wallLine.geometry.coordinates].map(
      point => new Vector3(point[0], point[1]),
    )

    return wallEnds.every(wallEnd => wallEnd.distanceTo(intersectedPoint) > wallOverlapThreshold)
  })
}

/**
 * Converts wall shape points to XY equivalent coordinates
 * Based on the assumptions:
 * - p0 -> (0, 0)
 * - p1 -> (0, distance(p0, p1))
 * - p2...end-1 -> (xyDistance(p0, pi), pi.z - p0.z)
 */
const convertWallPointsToXYEquivalent = (
  points: ImmutableVector3[],
): Array<{ x: number; y: number }> => {
  if (points.length < 2) return []

  const p0 = points[0]
  const xyEquivalent: Array<{ x: number; y: number }> = []

  // p0 -> (0, 0)
  xyEquivalent.push({ x: 0, y: 0 })

  // p1 -> (0, distance(p0, p1))
  if (points.length > 1) {
    const p1 = points[1]

    const distanceP0P1 = p0.distanceTo(p1)
    xyEquivalent.push({ x: distanceP0P1, y: 0 })
  }

  // p2...end-1 -> (xyDistance(p0, pi), pi.z - p0.z)
  for (let i = 2; i < points.length - 1; i++) {
    const pi = points[i]
    // Calculate xyDistance: distance in XY plane only
    const dx = pi.x - p0.x
    const dy = pi.y - p0.y
    const xyDistance = Math.sqrt(dx * dx + dy * dy)
    const zDiff = pi.z - p0.z
    xyEquivalent.push({ x: xyDistance, y: zDiff })
  }

  // Last point (if exists and not already processed)
  if (points.length > 2) {
    const pLast = points[points.length - 1]
    const dx = pLast.x - p0.x
    const dy = pLast.y - p0.y
    const xyDistance = Math.sqrt(dx * dx + dy * dy)
    const zDiff = pLast.z - p0.z
    xyEquivalent.push({ x: xyDistance, y: zDiff })
  }

  return xyEquivalent
}

const isWallType = (type: ElementTypes): type is 'inner_walls' | 'outer_walls' =>
  type === 'inner_walls' || type === 'outer_walls'

const hasIncomingNonRipTargets = (
  wallGuid: string,
  graph: VerticalTransmissionGraph,
  guidToElementType: Record<string, ElementTypes | undefined>,
): boolean => {
  const wallTargetGuids = graph.element_targets
    .filter(target => target.element_guid === wallGuid)
    .map(target => target.guid)

  if (wallTargetGuids.length === 0) return false

  const wallTargetGuidSet = new Set(wallTargetGuids)

  let hasAnyIncomingEdge = false
  for (const { support_guid, target_guid } of graph.support_targets) {
    if (!wallTargetGuidSet.has(target_guid)) continue
    hasAnyIncomingEdge = true

    const support = graph.element_supports.find(s => s.guid === support_guid)
    const sourceElementGuid = support?.element_guid

    // If we can't resolve the type we assume it's relevant (non-rip)
    const sourceType = sourceElementGuid ? guidToElementType[sourceElementGuid] : undefined
    if (sourceType !== 'rips') return true
  }

  // If the wall is marked as a target but we can't resolve any incoming edge,
  // we conservatively assume it's load-bearing and should show the warning.
  return !hasAnyIncomingEdge
}

const shouldShowWallSupportConfigurationProblem = (
  wallGuid: string,
  graph: VerticalTransmissionGraph,
  stiffeningSegments: StiffeningSegment[],
  elementsWithManualLoads: Set<string>,
  guidToElementType: Record<string, ElementTypes | undefined>,
): boolean =>
  hasIncomingNonRipTargets(wallGuid, graph, guidToElementType) ||
  stiffeningSegments.some(segment => segment.element_guid === wallGuid) ||
  elementsWithManualLoads.has(wallGuid)

const getCircularVerticalTargetReferences = (
  graph: VerticalTransmissionGraph,
): Record<string, string[]> => {
  const supportGuidToSourceElement: Record<string, string> = {}
  for (const support of graph.element_supports) {
    supportGuidToSourceElement[support.guid] = support.element_guid
  }

  const targetGuidToTargetElement: Record<string, string> = {}
  for (const target of graph.element_targets) {
    targetGuidToTargetElement[target.guid] = target.element_guid
  }

  const referencesByElement: Record<string, Set<string>> = {}
  const circularReferencesByElement: Record<string, Set<string>> = {}

  for (const { support_guid, target_guid } of graph.support_targets) {
    const sourceElementGuid = supportGuidToSourceElement[support_guid]
    const targetElementGuid = targetGuidToTargetElement[target_guid]

    if (
      isUndefined(sourceElementGuid) ||
      isUndefined(targetElementGuid) ||
      sourceElementGuid === targetElementGuid
    ) {
      continue
    }

    const existingTargetReferences =
      referencesByElement[sourceElementGuid] ?? (referencesByElement[sourceElementGuid] = new Set())

    existingTargetReferences.add(targetElementGuid)

    if (referencesByElement[targetElementGuid]?.has(sourceElementGuid)) {
      ;(circularReferencesByElement[sourceElementGuid] ??= new Set()).add(targetElementGuid)
      ;(circularReferencesByElement[targetElementGuid] ??= new Set()).add(sourceElementGuid)
    }
  }

  const circularReferences: Record<string, string[]> = {}
  for (const [elementGuid, targetGuids] of Object.entries(circularReferencesByElement)) {
    circularReferences[elementGuid] = [...targetGuids]
  }

  return circularReferences
}

const getLabelForGuid = (guid: string, guidToLabel: Record<string, string>): string =>
  guidToLabel[guid] || guid

/**
 * Checks if a 2D polygon is convex by checking if all consecutive turns have the same sign
 * Returns true if convex, false otherwise
 */
const isConvexPolygon = (points: Array<{ x: number; y: number }>): boolean => {
  if (points.length < 3) return true // Degenerate cases are considered convex

  let sign: number | null = null

  for (let i = 0; i < points.length; i++) {
    const p0 = points[i]
    const p1 = points[(i + 1) % points.length]
    const p2 = points[(i + 2) % points.length]

    // Calculate cross product to determine turn direction
    // (p1 - p0) x (p2 - p1)
    const v1x = p1.x - p0.x
    const v1y = p1.y - p0.y
    const v2x = p2.x - p1.x
    const v2y = p2.y - p1.y

    const crossProduct = v1x * v2y - v1y * v2x

    // Skip if cross product is zero (collinear points, angle == 180°)
    // According to assumptions, this shouldn't happen, but we handle it
    if (Math.abs(crossProduct) < 1e-10) continue

    const currentSign = Math.sign(crossProduct)

    if (sign === null) {
      sign = currentSign
    } else if (sign !== currentSign) {
      // Different signs mean the polygon is not convex
      return false
    }
  }

  return true
}

const hasMoreThanNDecimals = (value: number, decimals: number): boolean => {
  const rounded = Number(value.toFixed(decimals))
  return Math.abs(value - rounded) > 1e-9
}

/**
 * Inspects the current model together with the vertical transmission graph and
 * collects all element-level validation findings.
 *
 * The checks cover support configurations, geometry constraints, roof/slab
 * point quality, indirect support situations for rips, and whether selected
 * load-bearing elements receive any incoming or manually assigned loads.
 *
 * Returns both the full list of detected problems and the unique element GUIDs
 * of all affected elements so the caller can display or highlight them.
 */
const getProblematicElements = (
  graph: VerticalTransmissionGraph,
  guidToElementType: Record<string, ElementTypes | undefined>,
  positionGuidToWallGuid: Record<string, string>,
  domainGuidToDomain: Record<string, Domain>,
  loadsPerElement: ElementLoad[],
  guidToElement: Record<string, ShapeObject>,
  stiffeningSegments: StiffeningSegment[] = [],
  guidToLabel: Record<string, string> = {},
): ProblematicElements => {
  const problems: ProblemOnElement[] = []
  const circularVerticalTargetReferences = getCircularVerticalTargetReferences(graph)
  const hasTransmissionResults = graph.element_targets.length > 0
  const targetGuidToElementGuid = graph.element_targets.reduce<Record<string, string>>(
    (collector, target) => {
      collector[target.guid] = target.element_guid
      return collector
    },
    {},
  )
  const elementsWithIncomingGraphLoads = graph.support_targets.reduce<Set<string>>(
    (collector, { target_guid }) => {
      const elementGuid = targetGuidToElementGuid[target_guid]
      if (!isUndefined(elementGuid)) {
        collector.add(elementGuid)
      }
      return collector
    },
    new Set<string>(),
  )
  const elementsWithManualLoads = loadsPerElement.reduce<Set<string>>((collector, load) => {
    collector.add(load.element_guid)
    return collector
  }, new Set<string>())

  Object.entries(guidToElementType).forEach(([guid, type]) => {
    if (!type) return

    const circularReferenceTargets = circularVerticalTargetReferences[guid]

    if (!isUndefined(circularReferenceTargets) && circularReferenceTargets.length > 0) {
      const referencedElements = circularReferenceTargets
        .map(targetGuid => getLabelForGuid(targetGuid, guidToLabel))
        .join(', ')
      const message = `Element ist Teil einer zirkulären Referenz im vertikalen Lastabtrag mit: ${referencedElements}.`

      problems.push({
        element_guid: guid,
        message,
        severity: 'error',
      })
    }

    const supports = filter(graph.element_supports, { element_guid: guid })
    const admissibleConfigs = ADMISSIBLE_SUPPORTS[type]

    if (admissibleConfigs) {
      const pointSupports = supports.filter(s => s.support_type === 'point')
      const lineSupports = supports.filter(s => s.support_type === 'line')

      // Group line supports by domain
      const domainSupports = groupBy(lineSupports, 'domain_guid')

      const domainToFullySupported = Object.entries(domainSupports).map(
        ([domain_guid, lineSupports]) => {
          const intervals = lineSupports.map(s => s.relative_interval as Interval)
          const domain = domainGuidToDomain[domain_guid]
          const result = checkIntervalUnionCoversCompleteWidth(intervals, domain?.length)
          return {
            domain_guid,
            isValid: result.isValid,
            errorMessage: result.errorMessage,
          }
        },
      )
      const fullySupportedDomains = domainToFullySupported.filter(d => d.isValid).length

      const hasPartiallySupportedDomain = domainToFullySupported.some(d => !d.isValid)

      const isValid = admissibleConfigs.some(config => {
        const isPointSupportsValid =
          config.minPointSupports <= pointSupports.length &&
          pointSupports.length <= config.maxPointSupports
        const isLineSupportsValid =
          config.minLineSupports <= lineSupports.length &&
          lineSupports.length <= config.maxLineSupports
        const isFullySupportedDomainsValid =
          config.minFullySupportedDomains <= fullySupportedDomains &&
          fullySupportedDomains <= config.maxFullySupportedDomains
        const isPartiallySupportedDomainsValid =
          config.partiallySupportedDomainsAllowed || !hasPartiallySupportedDomain
        return (
          isPointSupportsValid &&
          isLineSupportsValid &&
          isFullySupportedDomainsValid &&
          isPartiallySupportedDomainsValid
        )
      })

      if (!isValid) {
        const domainErrors = domainToFullySupported.filter(d => !d.isValid)
        const validConfigs = admissibleConfigs.map(config => config.note).join(' ODER ')
        let message = `Element hat keine gültige Auflagerkonfiguration${
          domainErrors.length > 0 ? `: ${domainErrors[0].errorMessage} ` : '. '
        }Gültige Konfigurationen sind: ${validConfigs}`
        const isWallSupportConfigurationProblem = isWallType(type)

        if (hasPartiallySupportedDomain) {
          message +=
            ' Ein oder mehrere Domains sind nur teilweise unterstützt, was für diese Konfiguration nicht zulässig ist.'
        }

        if (isWallSupportConfigurationProblem) {
          const shouldShowWarning = shouldShowWallSupportConfigurationProblem(
            guid,
            graph,
            stiffeningSegments,
            elementsWithManualLoads,
            guidToElementType,
          )

          if (shouldShowWarning) {
            problems.push({
              element_guid: guid,
              message: message,
              severity: 'error',
            })
          }
        } else {
          problems.push({
            element_guid: guid,
            message: message,
            severity: 'error',
          })
        }
      }

      if (type === 'rips' && isValid) {
        const isRipUnsupported = checkIfRipIsIndirectlyUnsupported(
          supports[0],
          graph,
          positionGuidToWallGuid[guid],
        )
        if (isRipUnsupported) {
          const message = `Rippe ist verbunden mit der Wand, aber diese hat keine Auflager an dieser Stelle.`
          problems.push({
            element_guid: guid,
            message: message,
            severity: 'error',
          })
        }
      }
    }

    // Check if points within shapes are too close to each other
    if (
      type === 'roof_slabs' ||
      type === 'slabs' ||
      type === 'vertical_roof_slabs' ||
      type === 'vertical_slabs'
    ) {
      if (guid in guidToElement) {
        const slab = guidToElement[guid]
        const slabPoints = slab.shape.points

        const distanceThreshold = 0.2

        const anyPointNearEachOther = some(slabPoints, (point, index) => {
          const nextPoint = nthLooped(slabPoints, index + 1)
          const distance = point.distanceTo(nextPoint)
          return distance <= distanceThreshold
        })

        if (anyPointNearEachOther) {
          problems.push({
            element_guid: guid,
            message: `Mindestens 2 Punkte des Elements sind sehr nah beieinander (innerhalb von ${distanceThreshold}m).`,
            severity: 'warning',
          })
        }
      }
    }

    // Check if walls are strictly xy aligned
    if ((type === 'inner_walls' || type === 'outer_walls') && guid in guidToElement) {
      const wall = guidToElement[guid]

      if (isWallIntersectingWithOtherWalls(wall, guidToElement, guidToElementType)) {
        problems.push({
          element_guid: guid,
          message: 'Wand darf keine anderen Wände schneiden',
          severity: 'error',
        })
      }

      const wallStart = wall.shape.points[0]
      const wallEnd = wall.shape.points[1]
      const isDiagonal = wallStart.x !== wallEnd.x && wallStart.y !== wallEnd.y

      if (isDiagonal) {
        const message = `Wand ist nicht streng xy-ausgerichtet.`
        problems.push({
          element_guid: guid,
          message: message,
          severity: 'warning',
        })
      }

      // Check if wall shape is convex
      const wallPoints = wall.shape.points
      if (wallPoints.length >= 3) {
        const xyEquivalent = convertWallPointsToXYEquivalent(wallPoints)
        const isConvex = isConvexPolygon(xyEquivalent)

        if (!isConvex) {
          problems.push({
            element_guid: guid,
            message:
              'Wandform ist nicht konvex. Dies ist nicht empfohlen und kann zu Fehlern bei der Berechnung führen.',
            severity: 'error',
          })
        }
      }
    }

    if (
      (type === 'vertical_roof_slabs' ||
        type === 'vertical_slabs' ||
        type === 'roof_slabs' ||
        type === 'slabs') &&
      guid in guidToElement
    ) {
      const slab = guidToElement[guid]
      const slabPoints = slab.shape.points

      const isNotXYInclined = !isOnlyXYInclined(slabPoints)

      if (isNotXYInclined) {
        problems.push({
          element_guid: guid,
          message: 'Dach/Decke/Position ist nicht streng xy-geneigt.',
          severity: 'warning',
        })
      }
    }

    if (type === 'roof_slabs' && guid in guidToElement) {
      const roofSlab = guidToElement[guid]
      const roofSlabPoints = roofSlab.shape.points

      // Check for x,y coordinates with more than 2 decimal places
      const hasXYCoordinatesWithMoreThan2Decimals = roofSlabPoints.some(point => {
        const xDecimals = (point.x.toString().split('.')[1] || '').length
        const yDecimals = (point.y.toString().split('.')[1] || '').length
        return xDecimals > 2 || yDecimals > 2
      })

      if (hasXYCoordinatesWithMoreThan2Decimals) {
        problems.push({
          element_guid: guid,
          message: 'Dach enthält X/Y Koordinaten mit mehr als 2 Dezimalstellen',
          severity: 'warning',
        })
      }

      // Check for z coordinates with more than 2 decimal places
      const hasZCoordinatesWithMoreThan2Decimals = roofSlabPoints.some(point => {
        const zDecimals = (point.z.toString().split('.')[1] || '').length
        return zDecimals > 2
      })

      if (hasZCoordinatesWithMoreThan2Decimals) {
        problems.push({
          element_guid: guid,
          message: 'Dach enthält Z Koordinaten mit mehr als 2 Dezimalstellen',
          severity: 'warning',
        })
      }

      // Check for adjacent points with distance <= 0.1
      const hasAdjacentPointsTooClose = roofSlabPoints.some((point, index) => {
        const nextIndex = (index + 1) % roofSlabPoints.length
        const nextPoint = roofSlabPoints[nextIndex]

        const v1 = new Vector3(point.x, point.y, point.z)
        const v2 = new Vector3(nextPoint.x, nextPoint.y, nextPoint.z)
        const distance = v1.distanceTo(v2)

        return distance <= 0.1
      })

      if (hasAdjacentPointsTooClose) {
        problems.push({
          element_guid: guid,
          message: 'Dach enthält benachbarte Punkte mit einem Abstand kleiner oder gleich 0,1m',
          severity: 'warning',
        })
      }
    }

    if ((type === 'inner_walls' || type === 'outer_walls') && guid in guidToElement) {
      const wall = guidToElement[guid]
      const openings = wall.openings ?? []

      openings.forEach(opening => {
        const hasXYCoordinatesWithMoreThan2Decimals = opening.shape.points.some(point => {
          return hasMoreThanNDecimals(point.x, 2) || hasMoreThanNDecimals(point.y, 2)
        })

        if (!hasXYCoordinatesWithMoreThan2Decimals) return

        problems.push({
          element_guid: opening.guid,
          message: 'Öffnung enthält X/Y Koordinaten mit mehr als 2 Dez.- Stellen',
          severity: 'warning',
        })
        problems.push({
          element_guid: wall.guid,
          message: 'Öffnung enthält X/Y Koordinaten mit mehr als 2 Dez.- Stellen',
          severity: 'warning',
        })
      })
    }

    // get all elements that do not have any load inducing elements
    if (
      hasTransmissionResults &&
      elementTypesNotAllowedWithoutLoadInducing.has(type) &&
      !elementsWithIncomingGraphLoads.has(guid) &&
      !elementsWithManualLoads.has(guid)
    ) {
      const message = `Element erhält weder Last(en) aus anderen Bauteilen noch manuell zugewiesene Lasten.`
      problems.push({
        element_guid: guid,
        message: message,
        severity: 'warning',
      })
    }
  })

  const problematicElements = problems.reduce((collector, { element_guid }) => {
    collector.add(element_guid)
    return collector as Set<string>
  }, new Set())
  const result = {
    problemsOnElements: problems,
    element_guids: [...problematicElements],
  } as ProblematicElements
  return result
}

export default getProblematicElements
